/**
 * Deal Engine API client.
 *
 * Wraps the cdk PR-2 Deal Manager bridge:
 *
 *   POST /deals/push-through-engine   open a deal + DealAnalysis, signal
 *                                     dealManagerWorkflow to start the
 *                                     Deal Processing Engine.
 *   POST /deals/re-analyze            re-run the engine on an existing deal.
 *   POST /deals/progress-events       cursor-tail of DealAnalysis.progressEvents
 *                                     for first-mount + reconnect replay.
 *
 * Shape decisions:
 *   - `progressStreamKey` is just the dealAnalysisId today (no separate token);
 *     keeping the field name lets us swap to a signed WebSocket token later
 *     without breaking the consumer interface.
 *   - Polling cadence on /deals/progress-events lives in the useDealProgress
 *     hook, mirroring the chat polling pattern. Real-time push lands when
 *     the WebSocket fabric does.
 */

import { createApiRequest } from "src/api/common/apiClient";

// ─── Request / response types ─────────────────────────────────────────

export interface PushDealThroughEngineRequest {
  /** Caller-supplied dedup token. Same key + same owner returns the prior dealId. */
  idempotencyKey: string;
  sourceFileId?: string;
  /** Existing Textract contractId — when supplied, engine skips re-running Textract. */
  contractId?: string;
  orgId?: string;
  title?: string;
  description?: string;
  metadata?: Record<string, unknown>;
  /** Frontend chat session — narration frames push to this WS subscriber when set. */
  chatSessionId?: string;
}

export interface PushDealThroughEngineResponse {
  dealId: string;
  dealVersionId: string;
  dealAnalysisId: string;
  /**
   * Stable id the read API (`/getContract`) and `/analysis/:contractId`
   * route key off. Derived backend-side from the source file's S3 object
   * basename so the read path can resolve back to the same DealAnalysis
   * row without an extra join. Null on the scripted re-analyze path
   * where the caller already has a contractId.
   */
  contractId: string | null;
  /** WebSocket subscription key — currently == dealAnalysisId. */
  progressStreamKey: string;
  reused: boolean;
  workflowId: string;
  acceptedAt: string;
}

export interface RequestReAnalysisRequest {
  dealId: string;
  contractId: string;
  chatSessionId?: string;
}

export interface RequestReAnalysisResponse {
  workflowId: string;
  dealAnalysisId: string;
  acceptedAt: string;
}

export interface ListProgressEventsRequest {
  dealAnalysisId: string;
  /** Return events strictly after this index. Default 0 (full replay). */
  afterIndex?: number;
}

export interface DealProgressEvent {
  /** 'SUMMARY' | 'STAKEHOLDER_ENRICHMENT' | 'EQ_ANALYSIS' | ... */
  stage: string;
  /** 'started' | 'in_progress' | 'completed' | 'failed' */
  status: string;
  /**
   * True only on a stage that ends the whole analysis — the successful
   * RENDER_CARDS event or a fatal failure that aborts the pipeline. A
   * swallowed enrichment failure (e.g. THREAD_INSIGHTS skipped) leaves
   * this falsy, so the overall status must NOT be derived as "failed"
   * from it. Set by the engine; see deal-processing-engine emitProgress.
   */
  terminal?: boolean;
  narration?: string;
  artifacts?: Record<string, unknown>;
  at: string;
}

export interface ListProgressEventsResponse {
  dealAnalysisId: string;
  events: DealProgressEvent[];
  /** Pass back as `afterIndex` on the next call. */
  nextAfterIndex: number;
  /** 'pending' | 'running' | 'complete' | 'failed' | 'unknown' */
  status: string;
}

// ─── API request functions ────────────────────────────────────────────

export const pushDealThroughEngineApi =
  createApiRequest<PushDealThroughEngineResponse>(
    "post",
    "/deals/push-through-engine",
  );

export const requestReAnalysisApi =
  createApiRequest<RequestReAnalysisResponse>("post", "/deals/re-analyze");

export const listProgressEventsApi =
  createApiRequest<ListProgressEventsResponse>(
    "post",
    "/deals/progress-events",
  );
